import {
  StoredEvent,
  EventManagerState,
  EventStats,
  EventSummary,
  EventFilter,
  TournamentUnion,
} from "../types/eventManager";
import { League } from "../types/league";

const STORAGE_KEY = "pickleballEventManager";

// Load event manager state from localStorage
export const loadEventManagerState = (): EventManagerState => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Convert date strings back to Date objects
      parsed.events = parsed.events.map((event: any) => ({
        ...event,
        createdAt: new Date(event.createdAt),
        updatedAt: new Date(event.updatedAt),
        lastAccessedAt: new Date(event.lastAccessedAt),
        data: {
          ...event.data,
          createdAt: new Date(event.data.createdAt),
          updatedAt: new Date(event.data.updatedAt),
        },
      }));
      return parsed;
    }
  } catch (error) {
    console.error("Error loading event manager state:", error);
  }

  return {
    events: [],
    activeEventId: null,
    recentEvents: [],
  };
};

// Save event manager state to localStorage
export const saveEventManagerState = (state: EventManagerState): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error("Error saving event manager state:", error);
  }
};

// Add a new event to the manager
export const addEvent = (
  state: EventManagerState,
  event: TournamentUnion | League,
  type: "tournament" | "league"
): EventManagerState => {
  const storedEvent: StoredEvent = {
    id: event.id,
    name: event.name,
    type,
    eventType:
      type === "tournament"
        ? (event as TournamentUnion).tournamentType
        : (event as League).leagueType,
    data: event,
    createdAt: event.createdAt,
    updatedAt: event.updatedAt,
    isActive: event.isActive,
    lastAccessedAt: new Date(),
  };

  const newState = {
    ...state,
    events: [...state.events, storedEvent],
    activeEventId: event.id,
    recentEvents: [
      event.id,
      ...state.recentEvents.filter((id) => id !== event.id),
    ].slice(0, 10),
  };

  saveEventManagerState(newState);
  return newState;
};

// Update an existing event
export const updateEvent = (
  state: EventManagerState,
  eventId: string,
  updatedEvent: TournamentUnion | League
): EventManagerState => {
  const newState = {
    ...state,
    events: state.events.map((event) =>
      event.id === eventId
        ? {
            ...event,
            data: updatedEvent,
            updatedAt: new Date(),
            lastAccessedAt: new Date(),
            isActive: updatedEvent.isActive,
          }
        : event
    ),
  };

  saveEventManagerState(newState);
  return newState;
};

// Remove an event
export const removeEvent = (
  state: EventManagerState,
  eventId: string
): EventManagerState => {
  const newState = {
    ...state,
    events: state.events.filter((event) => event.id !== eventId),
    activeEventId: state.activeEventId === eventId ? null : state.activeEventId,
    recentEvents: state.recentEvents.filter((id) => id !== eventId),
  };

  saveEventManagerState(newState);
  return newState;
};

// Set active event
export const setActiveEvent = (
  state: EventManagerState,
  eventId: string | null
): EventManagerState => {
  const newState = {
    ...state,
    activeEventId: eventId,
    recentEvents: eventId
      ? [eventId, ...state.recentEvents.filter((id) => id !== eventId)].slice(
          0,
          10
        )
      : state.recentEvents,
  };

  // Update last accessed time for the event
  if (eventId) {
    newState.events = newState.events.map((event) =>
      event.id === eventId ? { ...event, lastAccessedAt: new Date() } : event
    );
  }

  saveEventManagerState(newState);
  return newState;
};

// Get event by ID
export const getEventById = (
  state: EventManagerState,
  eventId: string
): StoredEvent | null => {
  return state.events.find((event) => event.id === eventId) || null;
};

// Get active event
export const getActiveEvent = (
  state: EventManagerState
): StoredEvent | null => {
  if (!state.activeEventId) return null;
  return getEventById(state, state.activeEventId);
};

// Calculate event statistics
export const calculateEventStats = (state: EventManagerState): EventStats => {
  const tournaments = state.events.filter(
    (event) => event.type === "tournament"
  );
  const leagues = state.events.filter((event) => event.type === "league");

  return {
    totalTournaments: tournaments.length,
    totalLeagues: leagues.length,
    activeTournaments: tournaments.filter((event) => event.isActive).length,
    activeLeagues: leagues.filter((event) => event.isActive).length,
    completedTournaments: tournaments.filter((event) => !event.isActive).length,
    completedLeagues: leagues.filter((event) => !event.isActive).length,
    totalEvents: state.events.length,
  };
};

// Get event summary
export const getEventSummary = (event: StoredEvent): EventSummary => {
  const data = event.data;
  let progress = 0;
  let participants = 0;
  let status: "active" | "completed" | "paused" = "active";

  if (event.type === "tournament") {
    const tournament = data as TournamentUnion;
    participants = tournament.teams.length;

    if (tournament.tournamentType === "single_elimination") {
      const bracket = tournament as any; // Type assertion for bracket
      if (bracket.champion) {
        progress = 100;
        status = "completed";
      } else {
        // Calculate progress based on completed matches
        const totalMatches =
          bracket.rounds?.reduce(
            (total: number, round: any) => total + round.matches.length,
            0
          ) || 0;
        const completedMatches =
          bracket.rounds?.reduce(
            (total: number, round: any) =>
              total +
              round.matches.filter((match: any) => match.isCompleted).length,
            0
          ) || 0;
        progress =
          totalMatches > 0 ? (completedMatches / totalMatches) * 100 : 0;
      }
    } else {
      // Speed ladder or classic ladder
      const rounds = (tournament as any).rounds || [];
      const totalRounds = tournament.totalRounds || 1;
      const currentRound = tournament.currentRound || 1;

      if (currentRound >= totalRounds) {
        progress = 100;
        status = "completed";
      } else {
        progress = (currentRound / totalRounds) * 100;
      }
    }
  } else {
    // League
    const league = data as League;

    // Get participants count based on league type
    if (league.leagueType === "round_robin") {
      const rrLeague = league as any;
      participants = rrLeague.teams?.length || 0;
      const totalMatches = rrLeague.stats?.totalMatches || 1;
      const completedMatches = rrLeague.stats?.completedMatches || 0;
      progress = (completedMatches / totalMatches) * 100;

      if (progress >= 100) {
        status = "completed";
      }
    } else {
      // For other league types, try to get teams from different properties
      const anyLeague = league as any;
      participants =
        anyLeague.teams?.length ||
        anyLeague.divisions?.reduce(
          (total: number, div: any) => total + (div.teams?.length || 0),
          0
        ) ||
        0;
      progress = 0; // Default for unimplemented league types
    }
  }

  if (!event.isActive) {
    status = "completed";
  }

  return {
    id: event.id,
    name: event.name,
    type: event.type,
    eventType: event.eventType,
    status,
    progress: Math.round(progress),
    participants,
    createdAt: event.createdAt,
    lastAccessedAt: event.lastAccessedAt,
    isActive: event.isActive,
  };
};

// Filter and sort events
export const filterAndSortEvents = (
  state: EventManagerState,
  filter: EventFilter = {}
): EventSummary[] => {
  let filteredEvents = state.events;

  // Apply filters
  if (filter.type) {
    filteredEvents = filteredEvents.filter(
      (event) => event.type === filter.type
    );
  }
  if (filter.eventType) {
    filteredEvents = filteredEvents.filter(
      (event) => event.eventType === filter.eventType
    );
  }

  // Convert to summaries
  let summaries = filteredEvents.map(getEventSummary);

  // Filter by status
  if (filter.status) {
    summaries = summaries.filter((summary) => summary.status === filter.status);
  }

  // Sort
  const sortBy = filter.sortBy || "accessed";
  const sortOrder = filter.sortOrder || "desc";

  summaries.sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case "name":
        comparison = a.name.localeCompare(b.name);
        break;
      case "created":
        comparison = a.createdAt.getTime() - b.createdAt.getTime();
        break;
      case "accessed":
        comparison = a.lastAccessedAt.getTime() - b.lastAccessedAt.getTime();
        break;
      case "progress":
        comparison = a.progress - b.progress;
        break;
      default:
        comparison = a.lastAccessedAt.getTime() - b.lastAccessedAt.getTime();
    }

    return sortOrder === "desc" ? -comparison : comparison;
  });

  return summaries;
};

// Get recent events
export const getRecentEvents = (
  state: EventManagerState,
  limit: number = 5
): EventSummary[] => {
  return state.recentEvents
    .slice(0, limit)
    .map((id) => getEventById(state, id))
    .filter((event): event is StoredEvent => event !== null)
    .map(getEventSummary);
};

// Export event data
export const exportEvent = (event: StoredEvent): string => {
  return JSON.stringify(
    {
      ...event,
      exportedAt: new Date(),
      version: "1.0",
    },
    null,
    2
  );
};

// Import event data
export const importEvent = (
  state: EventManagerState,
  importData: string
): { success: boolean; event?: StoredEvent; error?: string } => {
  try {
    const data = JSON.parse(importData);

    // Validate imported data
    if (!data.id || !data.name || !data.type || !data.eventType || !data.data) {
      return { success: false, error: "Invalid event data format" };
    }

    // Check for duplicate ID
    if (state.events.some((event) => event.id === data.id)) {
      return { success: false, error: "Event with this ID already exists" };
    }

    // Convert dates
    const importedEvent: StoredEvent = {
      ...data,
      createdAt: new Date(data.createdAt),
      updatedAt: new Date(data.updatedAt),
      lastAccessedAt: new Date(),
      data: {
        ...data.data,
        createdAt: new Date(data.data.createdAt),
        updatedAt: new Date(data.data.updatedAt),
      },
    };

    return { success: true, event: importedEvent };
  } catch (error) {
    return { success: false, error: "Failed to parse import data" };
  }
};

// Clear all events (with confirmation)
export const clearAllEvents = (): EventManagerState => {
  const newState: EventManagerState = {
    events: [],
    activeEventId: null,
    recentEvents: [],
  };

  saveEventManagerState(newState);
  return newState;
};
